import { gql } from '@apollo/client';

export const DEVICE_GQL = gql`
  query DeviceDetailsQuery($deviceId: uuid!, $now: bigint = "", $shortTimeWindow: bigint!, $longTimeWindow: bigint!) {
    device(where: { id: { _eq: $deviceId } }) {
      id
      serial
      type
      hw_version
      mac_address
      part_number
      algorithm_version
      device_algorithm_overrides(where: { deleted_at: { _is_null: true } }) {
        id
        algorithm_version
        algorithm {
          version
          description
          published_at
        }
      }
      device_data(
        where: {
          device_id: { _eq: $deviceId }
          _or: { ended_at: { _gte: $shortTimeWindow }, created_at: { _gte: $shortTimeWindow } }
        }
        order_by: { started_at: desc }
      ) {
        id
        id_from_device
        started_at
        ended_at
        created_at
        data
      }
      qa_reports(where: { device_id: { _eq: $deviceId } }, order_by: { created_at: desc }) {
        body
        status
        type
        id
        created_at
      }
      device_inductive_sensor_tests(where: { device_id: { _eq: $deviceId } }, order_by: { created_at: desc }) {
        started_at
        ended_at
        inductive_sensor_test_analyses(order_by: { created_at: desc }) {
          json_body
          status
          version
        }
      }
      factory_calibrations(where: { device_id: { _eq: $deviceId } }, order_by: { created_at: desc }) {
        started_at
        ended_at
        id
        factory_calibration_analyses(order_by: { created_at: desc }) {
          binary_body
          json_body
          status
          version
        }
      }
      lens_calibrations(
        where: { _and: [{ device_id: { _eq: $deviceId } }, { deleted_at: { _is_null: true } }] }
        order_by: { created_at: desc }
      ) {
        id
        started_at
        ended_at
      }
      device_assignments(where: { device_id: { _eq: $deviceId } }, order_by: { started_at: desc }, limit: 1) {
        started_at
        feed_line {
          name
          farm {
            id
            name
            barn_algorithm_overrides(where: { deleted_at: { _is_null: true } }) {
              id
              algorithm_version
              algorithm {
                version
                description
                published_at
              }
            }
          }
        }
      }
      bin_controller {
        bin_set {
          farm {
            id
            name
          }
        }
        bin_controller_bins(where: { deleted_at: { _is_null: true } }, order_by: { index: asc }) {
          index
          bin {
            name
          }
        }
      }
      firmware_branch {
        name
        firmwares(order_by: { created_at: desc }, limit: 1) {
          tag
        }
      }
      fwFaults: faults(
        where: { _and: [{ device_id: { _eq: $deviceId } }, { code: { _eq: 1001 } }] }
        order_by: { started_at: desc }
        limit: 1
      ) {
        created_at
        details
      }
      lastCompletedAuger: faults(
        where: { _and: [{ device_id: { _eq: $deviceId } }, { code: { _eq: 2002 } }, { ended_at: { _is_null: false } }] }
        order_by: { started_at: desc }
        limit: 1
      ) {
        started_at
      }
      augerRunning: faults(
        where: { _and: [{ device_id: { _eq: $deviceId } }, { code: { _eq: 2002 } }] }
        order_by: { started_at: desc }
        limit: 1
      ) {
        ended_at
      }
      lastReset: faults(
        where: {
          _and: [{ device_id: { _eq: $deviceId } }, { code: { _eq: 1001 } }, { started_at: { _gte: $longTimeWindow } }]
        }
        order_by: { started_at: desc }
        limit: 1
      ) {
        started_at
      }
      firstTransaction: transactions(
        where: { device_id: { _eq: $deviceId }, occured_at: { _gte: $longTimeWindow } }
        order_by: { occured_at: asc }
        limit: 1
      ) {
        occured_at
      }
      lastTransaction: transactions(
        where: { device_id: { _eq: $deviceId }, occured_at: { _gte: $longTimeWindow } }
        order_by: { occured_at: desc }
        limit: 1
      ) {
        occured_at
      }
      sampleTransaction: transactions(
        where: { device_id: { _eq: $deviceId }, occured_at: { _gte: $longTimeWindow } }
        order_by: { occured_at: desc }
        limit: 20
      ) {
        id
        occured_at
        type
        sub_type
        status
        external_id
        data
      }
      short_data_window_aggregate: device_data_aggregate(
        where: {
          _and: [
            { device_id: { _eq: $deviceId } }
            { started_at: { _gte: $shortTimeWindow, _lte: $now } }
            { duration: { _lte: 62, _gte: 58 } }
          ]
        }
      ) {
        aggregate {
          sum {
            started_at
            ended_at
          }
          count
        }
      }
      long_data_window_aggregate: device_data_aggregate(
        where: {
          _and: [
            { device_id: { _eq: $deviceId } }
            { started_at: { _gte: $longTimeWindow, _lte: $now } }
            { duration: { _lte: 62, _gte: 58 } }
          ]
        }
      ) {
        aggregate {
          sum {
            started_at
            ended_at
          }
          count
        }
      }
      recent_data: device_data(
        where: { _and: [{ device_id: { _eq: $deviceId } }, { started_at: { _gte: $shortTimeWindow } }] }
        order_by: { started_at: desc }
        limit: 3
      ) {
        data
        id_from_device
        ended_at
        created_at
      }
    }
  }
`;

export const GET_DEVICE_CONFIGURATION = gql`
  query GetDeviceConfiguration($deviceId: uuid!) {
    device_configuration(
      where: {
        device_id: { _eq: $deviceId }
        ended_at: { _is_null: true }
        deleted_at: { _is_null: true }
      }
      order_by: { started_at: desc }
      limit: 1
    ) {
      id
      device_id
      version
      started_at
      ended_at
      data
      created_at
      updated_at
    }
  }
`;

export const UPSERT_DEVICE_CONFIGURATION = gql`
  mutation UpsertDeviceConfiguration($deviceId: uuid!, $version: Int!, $startedAt: bigint!, $data: jsonb!) {
    insert_device_configuration_one(
      object: {
        device_id: $deviceId
        version: $version
        started_at: $startedAt
        ended_at: null
        data: $data
      }
    ) {
      id
      device_id
      version
      started_at
      ended_at
      data
      created_at
    }
  }
`;

export const CREATE_DEVICE_COMMAND = gql`
  mutation CreateDeviceCommand($deviceId: uuid!, $type: String!, $data: jsonb!) {
    insert_device_command_one(object: { device_id: $deviceId, type: $type, data: $data }) {
      id
      device_id
      type
      data
      created_at
      sent_at
      acknowledged_at
      error
    }
  }
`;

export const GET_DEVICE_COMMANDS = gql`
  query GetDeviceCommands($deviceId: uuid!, $from: bigint, $to: bigint) {
    device_command(
      where: {
        device_id: { _eq: $deviceId }
        created_at: { _gte: $from, _lte: $to }
        deleted_at: { _is_null: true }
      }
      order_by: { created_at: desc }
    ) {
      id
      type
      data
      created_at
      sent_at
      acknowledged_at
      error
    }
  }
`;
